import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { 
  ServiceCost, 
  ServiceCostFormData, 
  ServiceCostsFilter,
  ServiceCostCategory,
  ServiceCostCategoryFormData
} from '../types';

interface ServiceCostStore {
  // State
  costs: ServiceCost[];
  categories: ServiceCostCategory[];
  selectedCost: ServiceCost | null;
  loading: boolean;
  error: string | null;
  
  // Filters
  filters: ServiceCostsFilter;
  
  // Actions - Costs
  fetchCosts: () => Promise<void>;
  fetchCostById: (id: string) => Promise<void>;
  fetchCostsByService: (serviceId: string) => Promise<void>;
  fetchCostsByDeal: (dealId: string) => Promise<void>;
  createCost: (data: Partial<ServiceCostFormData>) => Promise<ServiceCost | null>;
  updateCost: (id: string, data: Partial<ServiceCostFormData>) => Promise<ServiceCost | null>;
  deleteCost: (id: string) => Promise<boolean>;
  approveCost: (id: string) => Promise<boolean>;
  
  // Actions - Categories
  fetchCategories: () => Promise<void>;
  createCategory: (data: Partial<ServiceCostCategoryFormData>) => Promise<ServiceCostCategory | null>;
  updateCategory: (id: string, data: Partial<ServiceCostCategoryFormData>) => Promise<ServiceCostCategory | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  
  // Utilities
  setFilters: (filters: ServiceCostsFilter) => void;
  clearFilters: () => void;
  setSelectedCost: (cost: ServiceCost | null) => void;
  clearError: () => void;
  
  // Margin calculations
  recalculateMargins: (serviceId: string, dealServiceId?: string) => Promise<boolean>;
  
  // Commission calculations
  recalculateCommissionsForDeal: (dealId: string) => Promise<boolean>;
  recalculateUserCommissionForDeal: (dealId: string) => Promise<boolean>;
  
  // Markup calculations
  calculateClientPrice: (baseAmount: number, markupType: 'none' | 'percentage' | 'fixed', markupPercentage?: number, markupAmount?: number) => number;
  applyMarkupToCost: (costId: string, markupType: 'none' | 'percentage' | 'fixed', markupPercentage?: number, markupAmount?: number) => Promise<boolean>;
  bulkApplyMarkup: (costIds: string[], markupType: 'none' | 'percentage' | 'fixed', markupPercentage?: number, markupAmount?: number) => Promise<boolean>;
  getCostBreakdown: (serviceId: string, dealServiceId?: string) => Promise<any>;
}

export const useServiceCostStore = create<ServiceCostStore>((set, get) => ({
  // Initial state
  costs: [],
  categories: [],
  selectedCost: null,
  loading: false,
  error: null,
  filters: {},

  // Actions - Costs
  fetchCosts: async () => {
    try {
      set({ loading: true, error: null });
      
      const { filters } = get();
      // Use actual_service_costs table for actual costs
      let query = supabase
        .from('actual_service_costs')
        .select(`
          *,
          service:services(id, title, list_price),
          deal:deals(id, title, client_id, clients(id, business_name))
        `)
        .order('created_at', { ascending: false });

      // Apply filters (use database field names)
      if (filters.search) {
        query = query.or(`cost_name.ilike.%${filters.search}%,cost_description.ilike.%${filters.search}%`);
      }
      
      if (filters.service_id) {
        query = query.eq('service_id', filters.service_id);
      }
      
      if (filters.deal_service_id) {
        query = query.eq('deal_service_id', filters.deal_service_id);
      }
      
      if (filters.cost_category_id) {
        query = query.eq('cost_category_id', filters.cost_category_id);
      }
      
      if (filters.cost_type) {
        query = query.eq('cost_type', filters.cost_type);
      }
      
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      
      if (filters.external_partner_id) {
        query = query.eq('external_partner_id', filters.external_partner_id);
      }
      
      if (filters.is_billable !== undefined) {
        query = query.eq('is_billable', filters.is_billable);
      }
      
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCosts = (data || []).map(cost => ({
        ...cost,
        // Map database fields to frontend fields
        name: cost.cost_name || cost.name,
        description: cost.cost_description || cost.description
      }));

      set({ costs: mappedCosts, loading: false });
    } catch (error) {
      console.error('Error fetching actual service costs:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei costi effettivi',
        loading: false 
      });
    }
  },

  fetchCostById: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_costs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCost = data ? {
        ...data,
        name: data.cost_name || data.name,
        description: data.cost_description || data.description
      } : null;

      set({ selectedCost: mappedCost, loading: false });
    } catch (error) {
      console.error('Error fetching service cost:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento del costo',
        loading: false 
      });
    }
  },

  fetchCostsByService: async (serviceId: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_costs')
        .select(`
          *,
          cost_category:service_cost_categories(id, name, category_type),
          created_by_user:user_profiles!service_costs_created_by_fkey(id, full_name, email)
        `)
        .eq('service_id', serviceId)
        // Only fetch template costs (those without deal_id) for service
        .is('deal_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCosts = (data || []).map(cost => ({
        ...cost,
        name: cost.cost_name || cost.name,
        description: cost.cost_description || cost.description
      }));

      set({ costs: mappedCosts, loading: false });
    } catch (error) {
      console.error('Error fetching service costs:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei costi del servizio',
        loading: false 
      });
    }
  },

  fetchCostsByDeal: async (dealId: string) => {
    try {
      set({ loading: true, error: null });

      // Use actual_service_costs table for deal costs
      const { data, error } = await supabase
        .from('actual_service_costs')
        .select(`
          *,
          service:services(id, title, list_price),
          cost_category:service_cost_categories(id, name, category_type)
        `)
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCosts = (data || []).map(cost => ({
        ...cost,
        name: cost.cost_name || cost.name,
        description: cost.cost_description || cost.description
      }));

      set({ costs: mappedCosts, loading: false });
    } catch (error) {
      console.error('Error fetching deal costs:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei costi del deal',
        loading: false 
      });
    }
  },

  createCost: async (data: Partial<ServiceCostFormData>) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // Map frontend fields to database fields - be explicit about what we include
      const costData = {
        // Core identification fields
        service_id: data.service_id,
        deal_id: data.deal_id || null,
        cost_category_id: data.cost_category_id,
        
        // Name and description (mapped to database fields)
        cost_name: data.name,
        cost_description: data.description,
        
        // Cost details
        cost_type: data.cost_type,
        calculation_type: data.calculation_type,
        cost_amount: data.estimated_amount || 0,
        
        // Amounts
        estimated_amount: data.estimated_amount || 0,
        actual_amount: data.actual_amount || 0,
        budgeted_amount: data.budgeted_amount || 0,
        
        // Units and rates
        estimated_units: data.estimated_units || 0,
        actual_units: data.actual_units || 0,
        unit_rate: data.unit_rate || 0,
        hourly_rate: data.hourly_rate || 0,
        hours_estimated: data.hours_estimated || 0,
        hours_actual: data.hours_actual || 0,
        
        // External partner
        external_partner_id: data.external_partner_id || null,
        partner_cost: data.partner_cost || 0,
        partner_markup_percentage: data.partner_markup_percentage || 0,
        
        // Status and flags
        status: data.status,
        is_billable: data.is_billable,
        affects_commission: data.deal_id ? (data.affects_commission || false) : false, // Only actual costs can affect commission
        
        // Dates
        incurred_date: data.incurred_date || null,
        due_date: data.due_date || null,
        
        // References
        invoice_reference: data.invoice_reference || null,
        
        // System fields
        organization_id: userProfile.organization_id,
        created_by: userData.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Determine which table to use based on deal_id presence
      const tableName = data.deal_id ? 'actual_service_costs' : 'service_costs';


      const { data: newCost, error } = await supabase
        .from(tableName)
        .insert(costData)
        .select('*')
        .single();

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCost = {
        ...newCost,
        name: newCost.cost_name || newCost.name,
        description: newCost.cost_description || newCost.description
      };

      // Add to local state
      set(state => ({
        costs: [mappedCost, ...state.costs],
        loading: false
      }));

      // Trigger margin recalculation
      await get().recalculateMargins(mappedCost.service_id, mappedCost.deal_service_id);

      return mappedCost;
    } catch (error) {
      console.error('Error creating service cost:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nella creazione del costo',
        loading: false 
      });
      return null;
    }
  },

  updateCost: async (id: string, data: Partial<ServiceCostFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // First find which table the cost is in
      let tableName = 'service_costs';
      let existingCost = null;

      // Try actual_service_costs first
      const { data: actualCost } = await supabase
        .from('actual_service_costs')
        .select('*')
        .eq('id', id)
        .single();

      if (actualCost) {
        tableName = 'actual_service_costs';
        existingCost = actualCost;
      } else {
        // Try service_costs (templates)
        const { data: templateCost } = await supabase
          .from('service_costs')
          .select('*')
          .eq('id', id)
          .single();
        
        if (templateCost) {
          tableName = 'service_costs';
          existingCost = templateCost;
        }
      }

      if (!existingCost) {
        throw new Error('Cost not found');
      }

      // Map frontend fields to database fields - be explicit about what we include
      const updateData = {
        // Core identification fields (don't update these usually, but just in case)
        ...(data.service_id && { service_id: data.service_id }),
        ...(data.deal_id !== undefined && { deal_id: data.deal_id || null }),
        ...(data.cost_category_id && { cost_category_id: data.cost_category_id }),
        
        // Name and description (mapped to database fields)
        ...(data.name !== undefined && { cost_name: data.name }),
        ...(data.description !== undefined && { cost_description: data.description }),
        
        // Cost details
        ...(data.cost_type && { cost_type: data.cost_type }),
        ...(data.calculation_type && { calculation_type: data.calculation_type }),
        ...(data.estimated_amount !== undefined && { cost_amount: data.estimated_amount || 0 }),
        
        // Amounts
        ...(data.estimated_amount !== undefined && { estimated_amount: data.estimated_amount || 0 }),
        ...(data.actual_amount !== undefined && { actual_amount: data.actual_amount || 0 }),
        ...(data.budgeted_amount !== undefined && { budgeted_amount: data.budgeted_amount || 0 }),
        
        // Units and rates
        ...(data.estimated_units !== undefined && { estimated_units: data.estimated_units || 0 }),
        ...(data.actual_units !== undefined && { actual_units: data.actual_units || 0 }),
        ...(data.unit_rate !== undefined && { unit_rate: data.unit_rate || 0 }),
        ...(data.hourly_rate !== undefined && { hourly_rate: data.hourly_rate || 0 }),
        ...(data.hours_estimated !== undefined && { hours_estimated: data.hours_estimated || 0 }),
        ...(data.hours_actual !== undefined && { hours_actual: data.hours_actual || 0 }),
        
        // External partner
        ...(data.external_partner_id !== undefined && { external_partner_id: data.external_partner_id || null }),
        ...(data.partner_cost !== undefined && { partner_cost: data.partner_cost || 0 }),
        ...(data.partner_markup_percentage !== undefined && { partner_markup_percentage: data.partner_markup_percentage || 0 }),
        
        // Status and flags
        ...(data.status && { status: data.status }),
        ...(data.is_billable !== undefined && { is_billable: data.is_billable }),
        ...(data.affects_commission !== undefined && { affects_commission: data.affects_commission || false }),
        
        // Dates
        ...(data.incurred_date !== undefined && { incurred_date: data.incurred_date || null }),
        ...(data.due_date !== undefined && { due_date: data.due_date || null }),
        
        // References
        ...(data.invoice_reference !== undefined && { invoice_reference: data.invoice_reference || null }),
        
        // System fields
        updated_by: userData.user.id,
        updated_at: new Date().toISOString()
      };

      const { data: updatedCost, error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw error;

      // Map database fields to frontend fields
      const mappedCost = {
        ...updatedCost,
        name: updatedCost.cost_name || updatedCost.name,
        description: updatedCost.cost_description || updatedCost.description
      };

      // Update local state
      set(state => ({
        costs: state.costs.map(cost => 
          cost.id === id ? mappedCost : cost
        ),
        selectedCost: state.selectedCost?.id === id ? mappedCost : state.selectedCost,
        loading: false
      }));

      // Trigger margin recalculation
      await get().recalculateMargins(mappedCost.service_id, mappedCost.deal_service_id);

      return mappedCost;
    } catch (error) {
      console.error('Error updating service cost:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento del costo',
        loading: false 
      });
      return null;
    }
  },

  deleteCost: async (id: string) => {
    try {
      set({ loading: true, error: null });

      // Get cost info for margin and commission recalculation
      const costToDelete = get().costs.find(c => c.id === id);

      // Try to delete from both tables
      let deleteSuccess = false;
      
      // Try actual_service_costs first
      const { error: actualError } = await supabase
        .from('actual_service_costs')
        .delete()
        .eq('id', id);

      if (!actualError) {
        deleteSuccess = true;
      } else {
        // Try service_costs (templates)
        const { error: templateError } = await supabase
          .from('service_costs')
          .delete()
          .eq('id', id);
        
        if (!templateError) {
          deleteSuccess = true;
        } else {
          throw templateError;
        }
      }

      if (!deleteSuccess) {
        throw new Error('Cost not found in any table');
      }

      // Update local state
      set(state => ({
        costs: state.costs.filter(cost => cost.id !== id),
        selectedCost: state.selectedCost?.id === id ? null : state.selectedCost,
        loading: false
      }));

      // Trigger margin recalculation if we had cost info
      if (costToDelete) {
        await get().recalculateMargins(costToDelete.service_id, costToDelete.deal_service_id);
        
        // Trigger commission recalculation if cost affected commissions
        if (costToDelete.affects_commission && costToDelete.deal_id) {
          await get().recalculateCommissionsForDeal(costToDelete.deal_id);
          await get().recalculateUserCommissionForDeal(costToDelete.deal_id);
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting service cost:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione del costo',
        loading: false 
      });
      return false;
    }
  },

  approveCost: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Approval only applies to actual costs (not templates)
      const { data: updatedCost, error } = await supabase
        .from('actual_service_costs')
        .update({
          is_approved: true,
          status: 'approved',
          approved_by: userData.user.id,
          approved_at: new Date().toISOString(),
          updated_by: userData.user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        costs: state.costs.map(cost => 
          cost.id === id ? { ...cost, is_approved: true, status: 'approved' } : cost
        ),
        selectedCost: state.selectedCost?.id === id 
          ? { ...state.selectedCost, is_approved: true, status: 'approved' }
          : state.selectedCost,
        loading: false
      }));

      // If this cost affects commission, recalculate both referrer and user commissions
      if (updatedCost.affects_commission && updatedCost.deal_id) {
        await get().recalculateCommissionsForDeal(updatedCost.deal_id);
        await get().recalculateUserCommissionForDeal(updatedCost.deal_id);
      }

      return true;
    } catch (error) {
      console.error('Error approving actual service cost:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'approvazione del costo effettivo',
        loading: false 
      });
      return false;
    }
  },

  // Actions - Categories
  fetchCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('service_cost_categories')
        .select('*')
        .eq('is_active', true)
        .order('category_type', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        // If table doesn't exist, return empty array instead of throwing
        console.warn('Service cost categories table not available:', error.message);
        set({ categories: [] });
        return;
      }

      set({ categories: data || [] });
    } catch (error) {
      console.error('Error fetching cost categories:', error);
      // Set empty categories instead of error state
      set({ categories: [] });
    }
  },

  createCategory: async (data: Partial<ServiceCostCategoryFormData>) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      const categoryData = {
        ...data,
        organization_id: userProfile.organization_id,
        created_by: userData.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: newCategory, error } = await supabase
        .from('service_cost_categories')
        .insert(categoryData)
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      set(state => ({
        categories: [...state.categories, newCategory].sort((a, b) => a.name.localeCompare(b.name))
      }));

      return newCategory;
    } catch (error) {
      console.error('Error creating cost category:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nella creazione della categoria'
      });
      return null;
    }
  },

  updateCategory: async (id: string, data: Partial<ServiceCostCategoryFormData>) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const updateData = {
        ...data,
        updated_by: userData.user.id,
        updated_at: new Date().toISOString()
      };

      const { data: updatedCategory, error } = await supabase
        .from('service_cost_categories')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        categories: state.categories.map(category => 
          category.id === id ? updatedCategory : category
        )
      }));

      return updatedCategory;
    } catch (error) {
      console.error('Error updating cost category:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento della categoria'
      });
      return null;
    }
  },

  deleteCategory: async (id: string) => {
    try {
      const { error } = await supabase
        .from('service_cost_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        categories: state.categories.filter(category => category.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting cost category:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione della categoria'
      });
      return false;
    }
  },

  // Utilities
  setFilters: (filters: ServiceCostsFilter) => {
    set({ filters });
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  setSelectedCost: (cost: ServiceCost | null) => {
    set({ selectedCost: cost });
  },

  clearError: () => {
    set({ error: null });
  },

  // Margin calculations
  recalculateMargins: async (serviceId: string, dealServiceId?: string) => {
    try {
      // Call the database function to recalculate margins
      const { error } = await supabase.rpc('calculate_service_margins', {
        p_service_id: serviceId,
        p_deal_service_id: dealServiceId || null,
        p_calculation_type: 'estimated'
      });

      if (error) throw error;

      // Also calculate actual margins
      const { error: actualError } = await supabase.rpc('calculate_service_margins', {
        p_service_id: serviceId,
        p_deal_service_id: dealServiceId || null,
        p_calculation_type: 'actual'
      });

      if (actualError) throw actualError;

      return true;
    } catch (error) {
      console.error('Error recalculating margins:', error);
      return false;
    }
  },

  // P12: i ricalcoli provvigioni al cambio costi sono event-driven — il trigger DB su
  // actual_service_costs notifica il motore in flusia-core, che riscrive la proiezione.
  // Questi metodi restano come no-op per compatibilità di interfaccia (nessuna scrittura
  // client-side su deal_referrers/deals: il vecchio micro-motore è stato rimosso col cutover V2).
  recalculateCommissionsForDeal: async (_dealId: string) => {
    return true;
  },

  recalculateUserCommissionForDeal: async (_dealId: string) => {
    return true;
  },

  // Calculate client price with markup
  calculateClientPrice: (baseAmount: number, markupType: 'none' | 'percentage' | 'fixed', markupPercentage = 0, markupAmount = 0) => {
    switch (markupType) {
      case 'percentage':
        return baseAmount * (1 + markupPercentage / 100);
      case 'fixed':
        return baseAmount + markupAmount;
      default:
        return baseAmount;
    }
  },

  // Apply markup to a single cost
  applyMarkupToCost: async (costId: string, markupType: 'none' | 'percentage' | 'fixed', markupPercentage = 0, markupAmount = 0) => {
    try {
      set({ loading: true, error: null });

      // Get current cost data
      const { data: currentCost, error: fetchError } = await supabase
        .from('actual_service_costs')
        .select('estimated_amount, actual_amount')
        .eq('id', costId)
        .single();

      if (fetchError) throw fetchError;

      const baseAmount = currentCost.actual_amount || currentCost.estimated_amount;
      const clientPrice = get().calculateClientPrice(baseAmount, markupType, markupPercentage, markupAmount);

      // Update cost with markup
      const { error: updateError } = await supabase
        .from('actual_service_costs')
        .update({
          markup_type: markupType,
          markup_percentage: markupType === 'percentage' ? markupPercentage : null,
          markup_amount: markupType === 'fixed' ? markupAmount : null,
          client_price: clientPrice,
          has_markup: markupType !== 'none'
        })
        .eq('id', costId);

      if (updateError) throw updateError;

      // Refresh costs
      await get().fetchCosts();
      return true;
    } catch (error) {
      console.error('Error applying markup to cost:', error);
      set({ error: friendlyDbError(error) || 'Error applying markup to cost' });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Apply markup to multiple costs
  bulkApplyMarkup: async (costIds: string[], markupType: 'none' | 'percentage' | 'fixed', markupPercentage = 0, markupAmount = 0) => {
    try {
      set({ loading: true, error: null });

      for (const costId of costIds) {
        const success = await get().applyMarkupToCost(costId, markupType, markupPercentage, markupAmount);
        if (!success) {
          throw new Error(`Failed to apply markup to cost ${costId}`);
        }
      }

      return true;
    } catch (error) {
      console.error('Error bulk applying markup:', error);
      set({ error: friendlyDbError(error) || 'Error bulk applying markup' });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Get cost breakdown for service/deal
  getCostBreakdown: async (serviceId: string, dealServiceId?: string) => {
    try {
      let query = supabase
        .from('actual_service_costs')
        .select(`
          *,
          cost_category:service_cost_categories(name, category_type)
        `)
        .eq('service_id', serviceId);

      if (dealServiceId) {
        query = query.eq('deal_service_id', dealServiceId);
      }

      const { data: costs, error } = await query;
      if (error) throw error;

      // Calculate breakdown
      const breakdown = {
        totalBaseCost: 0,
        totalMarkup: 0,
        totalClientPrice: 0,
        costsByCategory: {} as Record<string, any>,
        costsWithMarkup: 0,
        costsWithoutMarkup: 0
      };

      costs?.forEach(cost => {
        const baseAmount = cost.actual_amount || cost.estimated_amount;
        const clientPrice = cost.client_price || baseAmount;
        const markup = clientPrice - baseAmount;

        breakdown.totalBaseCost += baseAmount;
        breakdown.totalMarkup += markup;
        breakdown.totalClientPrice += clientPrice;

        if (cost.has_markup) {
          breakdown.costsWithMarkup += 1;
        } else {
          breakdown.costsWithoutMarkup += 1;
        }

        // Group by category
        const categoryType = cost.cost_category?.category_type || 'other';
        if (!breakdown.costsByCategory[categoryType]) {
          breakdown.costsByCategory[categoryType] = {
            baseCost: 0,
            markup: 0,
            clientPrice: 0,
            count: 0
          };
        }

        breakdown.costsByCategory[categoryType].baseCost += baseAmount;
        breakdown.costsByCategory[categoryType].markup += markup;
        breakdown.costsByCategory[categoryType].clientPrice += clientPrice;
        breakdown.costsByCategory[categoryType].count += 1;
      });

      return breakdown;
    } catch (error) {
      console.error('Error getting cost breakdown:', error);
      return null;
    }
  }
}));